/**
 * Combobox — the single autocomplete primitive (design/ux/01_foundations.md
 * §3.4, ARIA 1.2 combobox). Every picker (produit, fournisseur, compte,
 * caissier, facet filter, command palette) is a preset of this component;
 * the matching algorithm belongs to the server (A-11, SRCH).
 *
 *  · Remote (`loadOptions`): ≥ `minChars` (2) typed characters, debounce
 *    COMBOBOX_DEBOUNCE_MS, every request carries an AbortSignal and a
 *    sequence id — a late response for an older query, or after blur, is
 *    dropped and the list never re-opens by itself. « Recherche… » while a
 *    request is pending; never « Aucun résultat » before the answer.
 *  · Local (`options`): filtered in the browser, token-AND, accent- and
 *    case-insensitive.
 *  · Keys: ↑ ↓ Début Fin move · Entrée picks (one result: picks it; several
 *    and none active: stays in the field with « Choisissez … (↑ ↓ puis
 *    Entrée) ») · Tab picks the active option then leaves, else leaves keeping
 *    the text as a query · Échap closes, then clears · Alt+↓ opens with the
 *    current query · Alt+Entrée or ↓ past the last option = the create row.
 *  · `multiple`: facet filters — Entrée / click toggles, the list stays open,
 *    options carry their count (cross-module §3.3).
 *  · Scanner: `ref.runExact(code)` skips the debounce and asks an exact
 *    lookup (one match picked, several listed, none → create row). With
 *    `scanAware` the field detects wedge-scanner bursts itself (WP-17
 *    `useScanInput`): the terminator (Entrée / Tab) is swallowed and the
 *    exact lookup runs — or, with `onScan` (the counters' code resolver,
 *    cross-module XWP-25), the code is handed to the caller: the field gets
 *    back the text it held before the burst, the pending search is dropped and
 *    the list closes (the digits never stay as a query, AUD-BARC-04).
 *  · Matched tokens are rendered in bold (never colour alone).
 *  · Groups (WP-17): options carrying `group` are rendered under a group
 *    heading (ARIA listbox › group › option), in the order given.
 *  · Local + remote (WP-17, the command palette): with both `options` and
 *    `loadOptions`, the local options answer at once and the remote ones are
 *    appended when the query is searchable; while they load a status row
 *    reads `pendingText` (« Recherche des produits… ») under the local hits.
 *  · `inline`: the list is part of the surrounding dialog (always shown, not
 *    a popup); Échap is left to the dialog, which closes.
 *  · `onPickAlt`: Ctrl+Entrée on the active option (open in a new tab).
 */
import * as React from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { Icon, useField } from "./primitives";
import {
  COMBOBOX_DEBOUNCE_MS,
  COMBOBOX_MAX_VISIBLE,
  COMBOBOX_MIN_CHARS,
} from "@/config/ui.config.js";
import {
  comboboxKeyAction,
  enterAMettreEnAttente,
  issueEnterEnAttente,
  offreCreation,
  foldText,
  highlightSegments,
  isSearchable,
  listStatus,
  queryTokens,
} from "@/lib/combobox.js";
import { createRequestSequence, isAbortError } from "@/lib/request-sequence.js";
import { anchorStyle, useAnchorPosition } from "@/hooks/useAnchorPosition.js";
import { useScanInput } from "@/hooks/useScanInput.js";

export interface ComboboxOption {
  value: string;
  /** Line 1 (16 px / 600) — also the text put in the field on pick. */
  label: string;
  /** Line 2 (14 px muted meta: DCI · Forme · Laboratoire · stock). */
  meta?: string;
  /** Right-aligned figure (PPA, solde, facet count). */
  aside?: React.ReactNode;
  /** Facet count (multi-select filters). */
  count?: number;
  disabled?: boolean;
  /** Group heading the option is listed under (WP-17). */
  group?: string;
  /** Extra words matched by the local filter but not displayed. */
  keywords?: string;
}

export interface LoadOptionsContext {
  signal?: AbortSignal;
  /** A scanner burst: exact code lookup (no fuzzy matching). */
  exact?: boolean;
}

export interface ComboboxHandle {
  focus: () => void;
  /** Exact lookup that skips the debounce (scanner bursts, §4.8). */
  runExact: (code: string) => void;
}

export interface ComboboxProps<O extends ComboboxOption = ComboboxOption> {
  /** Accessible name when the combobox is not inside a Field. */
  label?: string;
  placeholder?: string;
  /** Selected value (single) — display text comes from `selectedLabel`. */
  value?: string | null;
  selectedLabel?: string;
  onChange?: (option: O | null) => void;
  /** Multi-select values (facet filters). */
  multiple?: boolean;
  values?: string[];
  onValuesChange?: (values: string[], toggled: O) => void;
  /** Local options (filtered in the browser). */
  options?: O[];
  /** Remote options. */
  loadOptions?: (query: string, ctx: LoadOptionsContext) => Promise<O[]>;
  minChars?: number;
  debounceMs?: number;
  /** Clear the field after a pick (search pickers that add a line). */
  resetOnPick?: boolean;
  emptyText?: (query: string) => string;
  /** How the search matches (A-11), shown under the empty message. */
  emptyHint?: string;
  noPickHint?: string;
  onCreate?: (query: string) => void;
  createLabel?: (query: string) => string;
  renderOption?: (o: O, s: { query: string; active: boolean; selected: boolean }) => React.ReactNode;
  disabled?: boolean;
  error?: boolean;
  size?: "sm" | "md" | "lg";
  id?: string;
  name?: string;
  autoFocus?: boolean;
  className?: string;
  /** Called with the current text when the field is left without a pick. */
  onQueryCommit?: (query: string) => void;
  /** Called on every keystroke with the field text. */
  onQueryChange?: (query: string) => void;
  /** Replaces the local token-AND filter (the caller ranks its own options). */
  filterOptions?: (options: O[], query: string) => O[];
  /** The list is part of the enclosing dialog (always shown; Échap closes the dialog). */
  inline?: boolean;
  /** Ctrl+Entrée on the active option. */
  onPickAlt?: (option: O) => void;
  /** Status row while the remote part of a local + remote list loads. */
  pendingText?: string;
  /** The first option is active as soon as there is one (Entrée opens the best match — palette). */
  autoActivateFirst?: boolean;
  /** Detect scanner bursts in this field and run the exact lookup (remote only). */
  scanAware?: boolean;
  /**
   * With `scanAware`: a burst is handed here instead of the exact lookup (XWP-25 — the counters ask
   * the one code resolver); the field gets its pre-burst text back and the list closes.
   */
  onScan?: (code: string) => void;
  /**
   * Entry pickers (ux/02 §4.7, AUD-UXDS-08): Entrée pressed while the search runs is kept and
   * applied to its answer (one result → picked; several → the first is highlighted, the list stays
   * open; none → the empty sentence), and Entrée with several results and none highlighted
   * highlights the first instead of the hint — it never picks `results[0]` blindly and never
   * leaves the field without a pick.
   */
  queueEnter?: boolean;
  /**
   * The create row is offered only once the server answered with no option (XWP-12: the counter's
   * « Signaler une demande » — a hit in flight or a listed product never raises a false demand).
   */
  createOnlyWhenEmpty?: boolean;
  /** Text of the failed-search row (its « Réessayer » button re-runs the query). */
  errorText?: string;
  /**
   * Controlled single picker (refonte WP-112, AUD-CP-17): leaving the field without a pick puts the
   * selected label back — the text never disagrees with the selection (a search typed over a chosen
   * product and abandoned leaves that product shown, not the half-typed query). Off by default.
   */
  keepSelectionOnBlur?: boolean;
}

/** Matched tokens in bold (never colour alone) — exported for `renderOption` presets. */
export function Highlight({ text, query }: { text: string; query: string }) {
  return (
    <>
      {highlightSegments(text, query).map((s, i) =>
        s.match ? <strong key={i}>{s.text}</strong> : <React.Fragment key={i}>{s.text}</React.Fragment>,
      )}
    </>
  );
}

function localFilter<O extends ComboboxOption>(options: O[], query: string): O[] {
  const tokens = queryTokens(query);
  if (tokens.length === 0) return options;
  return options.filter((o) => {
    const hay = foldText(`${o.label} ${o.meta ?? ""} ${o.keywords ?? ""}`);
    return tokens.every((t) => hay.includes(t));
  });
}

function ComboboxInner<O extends ComboboxOption>(
  props: ComboboxProps<O>,
  ref: React.Ref<ComboboxHandle>,
) {
  const {
    label,
    placeholder,
    value = null,
    selectedLabel,
    onChange,
    multiple,
    values = [],
    onValuesChange,
    options,
    loadOptions,
    minChars = loadOptions ? COMBOBOX_MIN_CHARS : 0,
    debounceMs = COMBOBOX_DEBOUNCE_MS,
    resetOnPick,
    emptyText = (q) => (q ? `Aucun résultat ne correspond à « ${q} »` : "Aucune option"),
    emptyHint,
    noPickHint = "Choisissez une option (↑ ↓ puis Entrée)",
    onCreate,
    createLabel = (q) => `Créer « ${q} »`,
    renderOption,
    disabled,
    error,
    size = "md",
    id,
    name,
    autoFocus,
    className,
    onQueryCommit,
    onQueryChange,
    filterOptions,
    inline,
    onPickAlt,
    pendingText = "Recherche…",
    autoActivateFirst = false,
    scanAware = false,
    onScan,
    queueEnter = false,
    createOnlyWhenEmpty = false,
    errorText = "La recherche a échoué.",
    keepSelectionOnBlur = false,
  } = props;
  const field = useField();
  const inputId = id ?? field?.id;
  const listId = React.useId();
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [query, setQuery] = React.useState(selectedLabel ?? "");
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(autoActivateFirst ? 0 : -1);
  const [remote, setRemote] = React.useState<O[]>([]);
  const [pending, setPending] = React.useState(false);
  const [loadError, setLoadError] = React.useState<unknown>(null);
  const [hint, setHint] = React.useState(false);
  const seqRef = React.useRef<ReturnType<typeof createRequestSequence> | null>(null);
  if (!seqRef.current) seqRef.current = createRequestSequence();
  const timer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const focused = React.useRef(false);
  // `queueEnter`: an Entrée pressed while the request runs, applied to its answer.
  const enterQueued = React.useRef(false);
  const isOpen = Boolean(inline) || open;
  const pos = useAnchorPosition(wrapRef, open && !inline, { matchWidth: true });

  // Keep the field text in step with an externally changed selection — also while the field has
  // the focus when the selected VALUE itself changed (a scan or a creation panel chose it, WP-24).
  const valeurPrecedente = React.useRef(value);
  React.useEffect(() => {
    const changee = valeurPrecedente.current !== value;
    valeurPrecedente.current = value;
    if (!multiple && (!focused.current || changee)) setQuery(selectedLabel ?? "");
  }, [selectedLabel, multiple, value]);

  React.useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      seqRef.current?.cancel();
    },
    [],
  );

  const hybrid = Boolean(loadOptions && options);
  const localQuery = multiple ? query : query === selectedLabel ? "" : query;
  const localList: O[] = options
    ? filterOptions
      ? filterOptions(options, localQuery)
      : localFilter(options, localQuery)
    : [];
  const list: O[] = hybrid ? [...localList, ...remote] : loadOptions ? remote : localList;
  const remoteStatus = loadOptions
    ? listStatus({ query, pending, count: remote.length, error: loadError, minChars, local: false })
    : null;
  // Hybrid: the local hits are pickable while the remote part loads.
  const status = hybrid
    ? list.length > 0
      ? "results"
      : remoteStatus === "idle"
        ? "empty"
        : remoteStatus
    : listStatus({
        query,
        pending,
        count: list.length,
        error: loadError,
        minChars,
        local: !loadOptions,
      });
  const trimmed = query.trim();
  const hasCreate = offreCreation({
    hasOnCreate: !!onCreate,
    query,
    pending,
    remote: Boolean(loadOptions),
    status,
    createOnlyWhenEmpty,
    minChars,
  });
  const selectedSet = new Set(multiple ? values.map(String) : value != null ? [String(value)] : []);

  const runSearch = React.useCallback(
    (q: string, exact = false) => {
      if (!loadOptions) return;
      const seq = seqRef.current!;
      const ticket = seq.begin();
      setPending(true);
      setLoadError(null);
      loadOptions(q, { signal: ticket.signal, exact }).then(
        (res) => {
          if (!seq.isCurrent(ticket) || !focused.current) return;
          setRemote(res ?? []);
          setPending(false);
          if (enterQueued.current) {
            enterQueued.current = false;
            const issue = issueEnterEnAttente((res ?? []).length);
            if (issue.type === "pick") {
              pickRef.current?.(res[0]);
              return;
            }
            setOpen(true);
            setActive(issue.type === "move" ? issue.index : -1);
            return;
          }
          if (exact) {
            if ((res ?? []).length === 1) {
              pickRef.current?.(res[0]);
              return;
            }
            setOpen(true);
          }
          if (autoActivateFirst) setActive((a) => (a < 0 ? 0 : a));
          else setActive((res ?? []).length === 1 ? 0 : -1);
        },
        (err) => {
          if (!seq.isCurrent(ticket) || isAbortError(err)) return;
          setPending(false);
          setLoadError(err);
        },
      );
    },
    [loadOptions, autoActivateFirst],
  );

  const schedule = (q: string) => {
    if (!loadOptions) return;
    if (timer.current) clearTimeout(timer.current);
    if (!isSearchable(q, minChars)) {
      seqRef.current?.cancel();
      setPending(false);
      setRemote([]);
      return;
    }
    setPending(true); // « Recherche… » from the first keystroke, never « Aucun résultat »
    timer.current = setTimeout(() => runSearch(q), debounceMs);
  };

  const pick = (o: O) => {
    if (o.disabled) return;
    setHint(false);
    if (multiple) {
      const k = String(o.value);
      const next = selectedSet.has(k) ? values.filter((v) => String(v) !== k) : [...values, o.value];
      onValuesChange?.(next, o);
      return;
    }
    onChange?.(o);
    setOpen(false);
    setActive(-1);
    seqRef.current?.cancel();
    setPending(false);
    setQuery(resetOnPick ? "" : o.label);
  };
  const pickRef = React.useRef(pick);
  pickRef.current = pick;

  const runExact = (code: string) => {
    if (timer.current) clearTimeout(timer.current);
    focused.current = true;
    setQuery(code);
    runSearch(code, true);
  };
  React.useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    runExact,
  }));
  // XWP-25 — a burst handed to the caller: whatever the burst typed is gone (the scan listener gave
  // the field its pre-burst text back), the search it started is dropped, the list closes.
  const onScanRef = React.useRef(onScan);
  onScanRef.current = onScan;
  const remettreAuScan = (code: string) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    seqRef.current?.cancel();
    enterQueued.current = false;
    setPending(false);
    setLoadError(null);
    setRemote([]);
    setOpen(false);
    setActive(autoActivateFirst ? 0 : -1);
    setHint(false);
    onScanRef.current?.(code);
  };
  useScanInput((code: string) => (onScanRef.current ? remettreAuScan(code) : runExact(code)), {
    inputRef,
    enabled: Boolean(scanAware && (loadOptions || onScan)),
    zone: Boolean(onScan),
  });

  const create = () => {
    if (!onCreate || !trimmed) return;
    setOpen(false);
    onCreate(trimmed);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (enterAMettreEnAttente(e, { queueEnter, pending, query, minChars })) {
      // ux/02 §4.7 — « Entrée while the search is running … is applied when the answer arrives ».
      e.preventDefault();
      enterQueued.current = true;
      setOpen(true);
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
        runSearch(query);
      }
      return;
    }
    const action = comboboxKeyAction(e, {
      open: isOpen,
      active,
      count: status === "results" ? list.length : 0,
      hasCreate,
      hasText: query.length > 0,
      hasAltPick: Boolean(onPickAlt),
      inline: Boolean(inline),
      enterHighlightsFirst: queueEnter,
    });
    if (!action) return;
    switch (action.type) {
      case "open":
        e.preventDefault();
        setOpen(true);
        // Alt+↓ also re-runs a failed search (the « Réessayer » key).
        if (loadOptions && isSearchable(query, minChars) && (remote.length === 0 || loadError) && !pending) runSearch(query);
        break;
      case "move":
        e.preventDefault();
        setActive(action.index ?? -1);
        setHint(false);
        break;
      case "pick":
        e.preventDefault();
        pick(list[action.index ?? 0]);
        break;
      case "pickAlt": {
        e.preventDefault();
        const o = list[action.index ?? 0];
        if (o && !o.disabled) onPickAlt?.(o);
        break;
      }
      case "pickAndLeave":
        pick(list[action.index ?? 0]);
        if (multiple) setOpen(false);
        break;
      case "create":
        e.preventDefault();
        create();
        break;
      case "hint":
        e.preventDefault();
        setHint(true);
        break;
      case "leave":
        setOpen(false);
        onQueryCommit?.(query);
        break;
      case "close":
        // Échap handled here: an enclosing dialog stays open.
        e.preventDefault();
        setOpen(false);
        setHint(false);
        break;
      case "clear":
        e.preventDefault();
        setQuery("");
        setRemote([]);
        seqRef.current?.cancel();
        setPending(false);
        if (!multiple && value != null) onChange?.(null);
        break;
      default:
        break;
    }
  };

  const optionId = (i: number) => `${listId}-opt-${i}`;
  const createIndex = status === "results" ? list.length : 0;
  const activeDescendant =
    isOpen && active >= 0
      ? active < list.length && status === "results"
        ? optionId(active)
        : hasCreate && active === createIndex
          ? `${listId}-create`
          : undefined
      : undefined;

  React.useEffect(() => {
    if (!activeDescendant) return;
    document.getElementById(activeDescendant)?.scrollIntoView?.({ block: "nearest" });
  }, [activeDescendant]);

  const describedBy = [field?.describedBy, hint ? `${listId}-hint` : null].filter(Boolean).join(" ") || undefined;
  // An inline list is part of its dialog: always rendered, so its empty
  // sentence (« Aucune page récente : tapez… ») is read before any typing.
  const showList =
    !disabled &&
    (Boolean(inline) ||
      (isOpen &&
        (hybrid ? list.length > 0 || remoteStatus !== "idle" : status !== "idle" || (!loadOptions && list.length > 0))));

  const renderOptionItem = (o: O, i: number) => {
    const selected = selectedSet.has(String(o.value));
    return (
      <li
        key={`${o.group ?? ""}:${o.value}`}
        id={optionId(i)}
        role="option"
        aria-selected={multiple ? selected : i === active}
        aria-disabled={o.disabled || undefined}
        className={cn(
          "pk-combobox-option",
          i === active && "pk-combobox-option--active",
          selected && "pk-combobox-option--selected",
        )}
        onMouseMove={() => setActive(i)}
        onClick={() => pick(o)}
      >
        {multiple && (
          <span className="pk-combobox-check" aria-hidden="true">
            {selected && <Icon name="check" size={14} />}
          </span>
        )}
        {renderOption ? (
          renderOption(o, { query, active: i === active, selected })
        ) : (
          <>
            <span className="pk-combobox-text">
              <span className="pk-combobox-line1">
                <Highlight text={o.label} query={query} />
              </span>
              {o.meta && (
                <span className="pk-combobox-line2">
                  <Highlight text={o.meta} query={query} />
                </span>
              )}
            </span>
            {o.aside != null && <span className="pk-combobox-aside">{o.aside}</span>}
            {o.count != null && <span className="pk-combobox-count">{o.count}</span>}
          </>
        )}
      </li>
    );
  };

  // Consecutive options sharing a `group` form one ARIA group with a heading.
  const grouped = list.some((o) => o.group);
  const sections: Array<{ group?: string; items: Array<{ o: O; i: number }> }> = [];
  if (status === "results") {
    list.forEach((o, i) => {
      const last = sections[sections.length - 1];
      if (last && last.group === o.group) last.items.push({ o, i });
      else sections.push({ group: o.group, items: [{ o, i }] });
    });
  }
  const remotePending = hybrid ? remoteStatus === "pending" : status === "pending";
  const remoteFailed = hybrid ? remoteStatus === "error" : status === "error";

  const listBox = (
    <ul
      id={listId}
      role="listbox"
      aria-label={label}
      aria-multiselectable={multiple || undefined}
      className={cn("pk-combobox-list", inline && "pk-combobox-list--inline")}
    >
      {grouped
        ? sections.map((sec, k) => (
            <li key={`g${k}`} role="presentation" className="pk-combobox-group">
              {sec.group && (
                <div id={`${listId}-g${k}`} className="pk-combobox-group-label" aria-hidden="true">
                  {sec.group}
                </div>
              )}
              <ul role="group" aria-labelledby={sec.group ? `${listId}-g${k}` : undefined} className="pk-combobox-group-list">
                {sec.items.map(({ o, i }) => renderOptionItem(o, i))}
              </ul>
            </li>
          ))
        : sections.flatMap((sec) => sec.items.map(({ o, i }) => renderOptionItem(o, i)))}
      {remotePending && (
        <li className="pk-combobox-status" role="presentation" aria-live="polite">
          <Icon name="loader-2" size={16} className="pk-combobox-spin" aria-hidden="true" />
          {hybrid ? pendingText : "Recherche…"}
        </li>
      )}
      {status === "empty" && (
        <li className="pk-combobox-status" role="presentation" aria-live="polite">
          <span>{emptyText(trimmed)}</span>
          {emptyHint && <span className="pk-combobox-line2">{emptyHint}</span>}
        </li>
      )}
      {remoteFailed && (
        <li className="pk-combobox-status pk-combobox-status--error" role="presentation" aria-live="assertive">
          <span>{errorText}</span>
          <span className="pk-combobox-retry">
            <button
              type="button"
              className="pk-combobox-retry-btn"
              tabIndex={-1}
              onClick={() => {
                inputRef.current?.focus();
                runSearch(query);
              }}
            >
              <Icon name="rotate-ccw" size={14} aria-hidden="true" />
              Réessayer
            </button>
            <kbd className="pk-kbd">Alt+↓</kbd>
          </span>
        </li>
      )}
      {hasCreate && (
        <li
          id={`${listId}-create`}
          role="option"
          aria-selected={active === createIndex}
          className={cn("pk-combobox-option pk-combobox-create", active === createIndex && "pk-combobox-option--active")}
          onMouseMove={() => setActive(createIndex)}
          onClick={create}
        >
          <Icon name="plus" size={16} aria-hidden="true" />
          <span className="pk-combobox-line1">{createLabel(trimmed)}</span>
          <kbd className="pk-kbd">Alt+Entrée</kbd>
        </li>
      )}
    </ul>
  );

  return (
    <div ref={wrapRef} className={cn("pk-combobox", className)}>
      <div className={cn("pk-combobox-field", `pk-combobox-field--${size}`, error && "pk-combobox-field--error")}>
        <Icon name={loadOptions ? "search" : "chevrons-up-down"} size={16} className="pk-combobox-icon" aria-hidden="true" />
        <input
          ref={inputRef}
          id={inputId}
          name={name}
          type="text"
          role="combobox"
          autoComplete="off"
          spellCheck={false}
          autoFocus={autoFocus}
          disabled={disabled}
          placeholder={placeholder}
          aria-label={field ? undefined : label}
          aria-autocomplete="list"
          aria-expanded={showList}
          aria-controls={listId}
          aria-activedescendant={activeDescendant}
          aria-describedby={describedBy}
          aria-invalid={error || field?.invalid || undefined}
          aria-required={field?.required || undefined}
          value={query}
          onChange={(e) => {
            const q = e.target.value;
            enterQueued.current = false;
            setQuery(q);
            onQueryChange?.(q);
            setOpen(true);
            setActive(autoActivateFirst ? 0 : -1);
            setHint(false);
            schedule(q);
          }}
          onFocus={() => {
            focused.current = true;
          }}
          onBlur={(e) => {
            if (e.relatedTarget && wrapRef.current?.contains(e.relatedTarget as Node)) return;
            focused.current = false;
            enterQueued.current = false;
            setOpen(false);
            setHint(false);
            if (timer.current) clearTimeout(timer.current);
            seqRef.current?.cancel();
            setPending(false);
            if (keepSelectionOnBlur && !multiple && value != null && query !== (selectedLabel ?? "")) setQuery(selectedLabel ?? "");
          }}
          onKeyDown={onKeyDown}
          onClick={() => !open && (!loadOptions || remote.length > 0) && setOpen(true)}
        />
        {pending && open && <Icon name="loader-2" size={16} className="pk-combobox-spin" aria-hidden="true" />}
      </div>
      {hint && (
        <p id={`${listId}-hint`} className="pk-combobox-hint" role="status">
          {noPickHint}
        </p>
      )}
      {showList &&
        (inline ? (
          <div className="pk-combobox-inline">{listBox}</div>
        ) : (
          pos &&
          createPortal(
            <div
              className="pk-combobox-popup"
              style={{ ...anchorStyle(pos), ["--pk-combobox-rows" as string]: COMBOBOX_MAX_VISIBLE }}
              onMouseDown={(e) => e.preventDefault()}
            >
              {listBox}
            </div>,
            document.body,
          )
        ))}
    </div>
  );
}

/** The one Combobox (generic over the option type). */
export const Combobox = React.forwardRef(ComboboxInner) as <O extends ComboboxOption = ComboboxOption>(
  props: ComboboxProps<O> & { ref?: React.Ref<ComboboxHandle> },
) => React.ReactElement;
